// src/components/Sidebar/SidebarBeat.jsx
// ─────────────────────────────────────────────────────────────────────────────
// BoneBeat modülüne özgü sidebar.
// MOBİL DÜZELTMESİ: transform inline style objesinde tutulur (CSS class değil).
// Bu CRA ortamında <style> interpolasyonunun güvenilmez davranışını önler.
//
// Z-Index hiyerarşisi:
//   hamburger : 10001
//   overlay   : 10000
//   drawer    : 9999
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect } from "react";
import { useNavigate }       from "react-router-dom";
import { useTranslation }    from "react-i18next";
import { useSidebarBeat }    from "../../context/SidebarBeatContext.jsx";
import { useAuth }           from "../../context/AuthContext.jsx";
import { useAdmin }          from "../../context/AdminContext.jsx";
import { logout }            from "../../firebase/auth.js";

const C = {
  bg:         "#100d07",
  bgDeep:     "#0a0803",
  border:     "rgba(212,168,83,0.4)",
  borderHov:  "rgba(212,168,83,0.6)",
  gold:       "#d4a853",
  goldDim:    "rgba(212,168,83,0.5)",
  goldGhost:  "rgba(212,168,83,0.18)",
  goldFaint:  "rgba(212,168,83,0.08)",
  ivory:      "var(--bone-text-primary)",
  ivoryDim:   "var(--bone-text-secondary)",
  ivoryGhost: "var(--bone-text-muted)",
  red:        "#e05c5c",
  redDim:     "rgba(224,92,92,0.5)",
  SIDEBAR_W:  260,
};

function GoldRule({ opacity = 0.12 }) {
  return (
    <div style={{
      height: 1, margin: "4px 20px",
      background: `linear-gradient(90deg,transparent,rgba(212,168,83,${opacity}),transparent)`,
    }} />
  );
}

function NavItem({ itemKey, activeView, label, sub, icon, onClick }) {
  const active = activeView === itemKey;
  const [hov, setHov] = React.useState(false);
  const lit = active || hov;

  return (
    <button
      onClick={() => onClick(itemKey)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 14,
        padding: "12px 20px", position: "relative",
        background: active ? "rgba(212,168,83,0.07)" : hov ? "rgba(212,168,83,0.04)" : "transparent",
        border: "none",
        borderLeft: `2px solid ${active ? C.gold : "transparent"}`,
        cursor: "pointer", transition: "all 0.3s ease",
      }}
    >
      <span style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "linear-gradient(90deg,rgba(212,168,83,0.05),transparent)",
        opacity: lit ? 1 : 0, transition: "opacity 0.3s ease",
      }} />
      <span style={{
        width: 1, height: 28, flexShrink: 0,
        background: `rgba(212,168,83,${lit ? 0.3 : 0.1})`,
        transition: "background 0.3s ease",
      }} />
      <span style={{
        color: lit ? C.goldDim : "rgba(212,168,83,0.22)",
        display: "flex", flexShrink: 0, transition: "color 0.3s ease",
      }}>{icon}</span>
      <span style={{ flex: 1, textAlign: "left" }}>
        <span style={{
          display: "block", fontFamily: "'Courier Prime',monospace",
          fontSize: 12, letterSpacing: "0.12em", marginBottom: 2,
          color: active ? C.ivory : hov ? C.ivoryDim : C.ivoryGhost,
          transition: "color 0.3s ease",
        }}>{label}</span>
        <span style={{
          display: "block", fontFamily: "'Courier Prime',monospace",
          fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase",
          color: lit ? "var(--bone-text-accent)" : "var(--bone-text-muted)",
          transition: "color 0.3s ease",
        }}>{sub}</span>
      </span>
      {active && (
        <span style={{
          width: 4, height: 4, borderRadius: "50%", flexShrink: 0,
          background: C.gold, boxShadow: `0 0 8px ${C.gold}`,
        }} />
      )}
    </button>
  );
}

function ProfileStrip({ user, userData }) {
  const initial = (user.displayName || user.email || "?")[0].toUpperCase();
  return (
    <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{
        width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
        border: `1px solid ${C.borderHov}`, background: "rgba(212,168,83,0.08)",
        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
      }}>
        {userData?.photoURL
          ? <img src={userData.photoURL} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 12, fontWeight: 700, color: C.goldDim }}>{initial}</span>
        }
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.ivoryDim,
          letterSpacing: "0.08em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{user.displayName || "Gezgin"}</div>
        <div style={{
          fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "rgba(212,168,83,0.3)",
          letterSpacing: "0.1em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{user.email}</div>
      </div>
    </div>
  );
}

export default function SidebarBeat({ activeView, onNavigate, isAdmin: isAdminProp }) {
  const { isOpen, openSidebar, closeSidebar } = useSidebarBeat();
  const { user, userData }                            = useAuth();
  const { isAdmin: ctxAdmin, loading }        = useAdmin();
  const navigate                              = useNavigate();

  const adminFlag = isAdminProp !== undefined ? isAdminProp : ctxAdmin;
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeSidebar(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeSidebar]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const handleLogout = async () => {
    try { await logout(); } catch {}
    closeSidebar();
    navigate("/");
  };

  function handleNav(key) { onNavigate(key); closeSidebar(); }

  const navItems = [
    {
      key: "feed", label: t('sidebar.beat.feed'), sub: t('sidebar.beat.feedSub'),
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}>
          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
        </svg>
      ),
    },
    {
      key: "mine", label: t('sidebar.beat.mine'), sub: t('sidebar.beat.mineSub'),
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}>
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      ),
    },
    {
      key: "new", label: t('sidebar.beat.new'), sub: t('sidebar.beat.newSub'),
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}>
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
        </svg>
      ),
    },
  ];

  const drawerStyle = {
    position: "fixed", top: 0, left: 0,
    width: C.SIDEBAR_W, height: "100vh",
    zIndex: 10000,   /* overlay(9998) üstünde, hamburger(10001) altında */
    background: C.bg, borderRight: `1px solid ${C.border}`,
    display: "flex", flexDirection: "column",
    overflowY: "auto", overflowX: "hidden",
    transform: isOpen ? "translateX(0)" : "translateX(-100%)",
    transition: "transform 0.38s cubic-bezier(0.16,1,0.3,1)",
  };

  return (
    <>
      <style>{`
        /* Tüm cihazlarda sidebar overlay — içerik her zaman tam genişlik */
        .bb-main {
          margin-left: 0 !important;
          width: 100% !important;
          padding-top: 64px !important;
        }
        .bb-nav-item {
          opacity: 0;
          animation: bb-slideIn 0.45s cubic-bezier(0.16,1,0.3,1) forwards;
        }
        @keyframes bb-slideIn {
          from { opacity: 0; transform: translateX(-10px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* ── Hamburger — sidebar kapalıyken her cihazda görünür ── */}
      <button
        onClick={openSidebar}
        aria-label={t('sidebar.beat.openMenu')}
        style={{
          display: isOpen ? "none" : "flex",
          position: "fixed", top: 14, left: 14,
          zIndex: 10001,
          width: 40, height: 40,
          background: C.bg, border: `1px solid ${C.border}`,
          color: C.goldDim, cursor: "pointer",
          alignItems: "center", justifyContent: "center",
          fontSize: 18, lineHeight: 1,
          transition: "color 0.2s, border-color 0.2s",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.color = C.gold; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = C.border;    e.currentTarget.style.color = C.goldDim; }}
      >
        ☰
      </button>

      {/* ── Overlay: sidebar açıkken arkayı karartır, drawer'ın altında ── */}
      {isOpen && (
        <div
          onClick={closeSidebar}
          aria-hidden="true"
          style={{
            position: "fixed", inset: 0,
            zIndex: 9998,   /* drawer(10000) altında — tıklamayı bloke etmez */
            background: "rgba(5,4,1,0.72)",
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
          }}
        />
      )}

      {/* ── Sidebar drawer ── */}
      <aside className="bb-drawer" style={drawerStyle}>
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>

          {/* Sağ kenar parıltısı */}
          <div style={{
            position: "absolute", top: 0, right: 0, width: 1, height: "100%",
            background: "linear-gradient(to bottom,transparent,rgba(212,168,83,0.18) 20%,rgba(212,168,83,0.18) 80%,transparent)",
            pointerEvents: "none", zIndex: 1,
          }} />

          {/* ── Başlık ── */}
          <div style={{
            height: 64, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "0 20px", borderBottom: `1px solid ${C.border}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg viewBox="0 0 24 24" fill="none" width={18} height={18}
                style={{ filter: "drop-shadow(0 0 5px rgba(212,168,83,0.5))" }}>
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" fill={C.gold} />
                <path d="M20 7h-2" stroke={C.gold} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
                <path d="M20 10h-2" stroke={C.gold} strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
                <path d="M20 13h-2" stroke={C.gold} strokeWidth="1.5" strokeLinecap="round" opacity="0.25" />
              </svg>
              <div>
                <span style={{
                  fontFamily: "'Playfair Display',serif",
                  fontSize: 15, fontWeight: 700, fontStyle: "italic",
                  color: C.goldDim, letterSpacing: "0.08em",
                  textShadow: "0 0 20px rgba(212,168,83,0.3)", display: "block",
                }}>BoneBeat</span>
                <span style={{
                  fontFamily: "'Courier Prime',monospace",
                  fontSize: 8, letterSpacing: "0.28em", textTransform: "uppercase",
                  color: "var(--bone-text-muted)",
                }}>Beat Archive</span>
              </div>
            </div>

            {/* Kapat (X) butonu */}
            <button onClick={closeSidebar} aria-label="Menüyü kapat"
              style={{
                width: 28, height: 28, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
                background: "transparent", border: `1px solid ${C.border}`,
                cursor: "pointer", color: "rgba(212,168,83,0.4)", transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.color = C.goldDim; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border;    e.currentTarget.style.color = "rgba(212,168,83,0.4)"; }}
            >
              <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* ── Navigasyon ── */}
          <nav style={{ flex: 1, overflowY: "auto", paddingBottom: 8 }}>

            <div style={{ padding: "14px 20px 8px" }}>
              <button onClick={() => { closeSidebar(); navigate("/"); }}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                  color: "var(--bone-text-accent)", fontFamily: "'Courier Prime',monospace",
                  fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase",
                  padding: 0, transition: "color 0.2s",
                }}
                onMouseEnter={e => e.currentTarget.style.color = C.goldDim}
                onMouseLeave={e => e.currentTarget.style.color = "var(--bone-text-accent)"}
              >
                ← {t('common.back')}
              </button>
            </div>

            <GoldRule opacity={0.08} />

            <div style={{ padding: "10px 20px 6px" }}>
              <span style={{
                fontFamily: "'Courier Prime',monospace", fontSize: 9,
                color: "var(--bone-text-muted)", textTransform: "uppercase", letterSpacing: "0.2em",
              }}>✦ BoneBeat ✦</span>
            </div>

            {navItems.map((item, idx) => (
              <div key={item.key} className="bb-nav-item" style={{ animationDelay: `${0.08 + idx * 0.07}s` }}>
                <NavItem itemKey={item.key} activeView={activeView}
                  label={item.label} sub={item.sub}
                  icon={item.icon} onClick={handleNav} />
              </div>
            ))}

            {/* Admin bölümü */}
            {!loading && adminFlag && (
              <React.Fragment>
                <GoldRule opacity={0.08} />
                <div style={{ padding: "10px 20px 4px" }}>
                  <span style={{
                    fontFamily: "'Courier Prime',monospace", fontSize: 9,
                    color: "rgba(224,92,92,0.4)", textTransform: "uppercase", letterSpacing: "0.2em",
                  }}>✦ Admin ✦</span>
                </div>
                <div className="bb-nav-item" style={{ animationDelay: "0.35s" }}>
                  <NavItem itemKey="moderation" activeView={activeView}
                    label={t('sidebar.modPanel')} sub={t('sidebar.modSub')}
                    onClick={handleNav}
                    icon={
                      <svg viewBox="0 0 24 24" fill="currentColor" width={16} height={16}>
                        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 4l5 2.18V11c0 3.5-2.33 6.79-5 7.93-2.67-1.14-5-4.43-5-7.93V7.18L12 5z" />
                      </svg>
                    }
                  />
                </div>
              </React.Fragment>
            )}
          </nav>

          {/* Profil şeridi */}
          {user && (<><GoldRule opacity={0.1} /><ProfileStrip user={user} userData={userData} /></>)}

          {/* Alt bilgi + Çıkış */}
          <div style={{
            borderTop: `1px solid ${C.border}`, flexShrink: 0,
            padding: "14px 20px", display: "flex", flexDirection: "column", gap: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 7 }}>
                <a href="https://www.instagram.com/bonecastofficial?igsh=MWJvYWdkc3E2bWE4bg=="
                  target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, border: `1px solid ${C.border}`, borderRadius: 6, color: "var(--bone-text-accent)", textDecoration: "none" }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                  </svg>
                </a>
                <a href="https://x.com/BoneCastOfcl" target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, border: `1px solid ${C.border}`, borderRadius: 6, color: "var(--bone-text-accent)", textDecoration: "none" }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
              </div>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "var(--bone-text-muted)", letterSpacing: "0.15em" }}>
                v1.0.4 · Beat
              </span>
            </div>

            {user && (
              <button onClick={handleLogout}
                style={{
                  width: "100%", background: "transparent",
                  border: "1px solid rgba(212,168,83,0.4)",
                  borderRadius: 8,
                  color: "var(--bone-text-accent)", padding: "9px 0",
                  fontFamily: "'Courier Prime',monospace", fontSize: 10,
                  letterSpacing: "0.22em", textTransform: "uppercase",
                  cursor: "pointer", transition: "all 0.2s ease",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(200,80,80,0.4)"; e.currentTarget.style.color = "rgba(230,100,100,0.8)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(212,168,83,0.4)"; e.currentTarget.style.color = "var(--bone-text-accent)"; }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width={11} height={11}>
                  <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                </svg>
                {t('auth.logout')}
              </button>
            )}

            <div style={{
              fontFamily: "'Courier Prime',monospace", fontSize: 8,
              color: "var(--bone-text-muted)", letterSpacing: "0.12em",
              lineHeight: 1.6, textAlign: "center",
            }}>
              {t('sidebar.copyright')}
            </div>
          </div>

        </div>
      </aside>
    </>
  );
}
